import { useEffect, useState, useMemo } from "react";
import Shell from "@/components/Shell";
import api, { API } from "@/lib/api";
import { Card } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Search, Trash2, ImageOff, RefreshCw, Download, CheckCircle2, AlertTriangle } from "lucide-react";
import { toast } from "sonner";
import PosterLightbox, { PosterThumb } from "@/components/PosterLightbox";

export default function AdminPosterRecords() {
  const [posters, setPosters] = useState([]);
  const [employees, setEmployees] = useState([]);
  const [languages, setLanguages] = useState([]);
  const [templates, setTemplates] = useState([]);
  const [mrId, setMrId] = useState("all");
  const [langFilter, setLangFilter] = useState("all");
  const [q, setQ] = useState("");
  const [date, setDate] = useState("");
  const [loading, setLoading] = useState(false);

  // per-row state
  const [regenerating, setRegenerating] = useState(null);
  const [downloading, setDownloading] = useState(null);
  const [viewing, setViewing] = useState(null); // poster open in the lightbox
  // per-row template override picker (for missing-template rows)
  const [rowTplOverride, setRowTplOverride] = useState({}); // { [poster_id]: template_id }

  // bulk assign state
  const [bulkTplId, setBulkTplId] = useState("");
  const [bulkBusy, setBulkBusy] = useState(false);

  useEffect(() => {
    api.get("/admin/employees?page_size=500").then(({ data }) => setEmployees(data.data)).catch(() => {});
    api.get("/languages").then(({ data }) => setLanguages(data)).catch(() => {});
    api.get("/templates").then(({ data }) => setTemplates(data)).catch(() => {});
  }, []);

  useEffect(() => {
    setLoading(true);
    const params = new URLSearchParams({ mine: "false" });
    if (mrId !== "all") params.set("mr_id", mrId);
    api.get(`/posters?${params}`)
      .then(({ data }) => setPosters(data))
      .catch(() => toast.error("Failed to load records"))
      .finally(() => setLoading(false));
  }, [mrId]);

  const templateIds = useMemo(() => new Set(templates.map((t) => t.id)), [templates]);

  const filtered = useMemo(() => posters.filter((p) => {
    if (p.deleted) return false;
    const okQ = !q || p.doctor_name.toLowerCase().includes(q.toLowerCase()) ||
      p.hospital?.toLowerCase().includes(q.toLowerCase()) ||
      p.created_by_name?.toLowerCase().includes(q.toLowerCase());
    const okD = !date || p.created_at?.startsWith(date);
    const okL = langFilter === "all" || p.language_code === langFilter;
    return okQ && okD && okL;
  }), [posters, q, date, langFilter]);

  const missingTplPosters = useMemo(
    () => filtered.filter((p) => !templateIds.has(p.template_id)),
    [filtered, templateIds],
  );
  const normalPosters = useMemo(
    () => filtered.filter((p) => templateIds.has(p.template_id)),
    [filtered, templateIds],
  );

  const handleDelete = async (id) => {
    if (!window.confirm("Delete this poster record?")) return;
    try {
      await api.delete(`/posters/${id}`);
      setPosters((prev) => prev.map((p) => p.id === id ? { ...p, deleted: true } : p));
      toast.success("Poster record deleted");
    } catch {
      toast.error("Failed to delete");
    }
  };

  const handleRegenerate = async (poster, overrideTplId) => {
    setRegenerating(poster.id);
    try {
      const body = overrideTplId ? { template_id: overrideTplId } : {};
      const { data } = await api.post(`/admin/posters/${poster.id}/regenerate`, body);
      setPosters((prev) => prev.map((p) => p.id === poster.id ? { ...p, ...data } : p));
      // clear override after successful regen
      setRowTplOverride((prev) => { const n = { ...prev }; delete n[poster.id]; return n; });
      toast.success("Poster regenerated — ready to download");
    } catch (err) {
      toast.error(err.response?.data?.detail || "Regeneration failed");
    } finally {
      setRegenerating(null);
    }
  };

  const handleDownload = async (poster) => {
    if (downloading) return;
    setDownloading(poster.id);
    try {
      const res = await fetch(`${API}/posters/${poster.id}/download?fmt=jpg`, { credentials: "include" });
      if (!res.ok) {
        const err = await res.json().catch(() => ({}));
        toast.error(err.detail || "Download failed");
        return;
      }
      const blob = await res.blob();
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = `${poster.doctor_name.replace(/ /g, "_")}_WHD.jpg`;
      a.click();
      URL.revokeObjectURL(url);
      // Local-disk posters are removed after the first download; CDN copies stay.
      if (!poster.poster_url) {
        setPosters((prev) => prev.map((p) => p.id === poster.id ? { ...p, file_deleted: true } : p));
      }
      toast.success("Download successful");
    } catch {
      toast.error("Download failed");
    } finally {
      setDownloading(null);
    }
  };

  const handleBulkAssign = async () => {
    if (!bulkTplId) { toast.error("Select a template first"); return; }
    const ids = missingTplPosters.map((p) => p.id);
    if (!ids.length) return;
    setBulkBusy(true);
    try {
      const { data } = await api.post("/admin/posters/bulk-assign-template", {
        poster_ids: ids,
        template_id: bulkTplId,
      });
      setPosters((prev) => prev.map((p) =>
        ids.includes(p.id) ? { ...p, template_id: bulkTplId } : p
      ));
      toast.success(`Template assigned to ${data.updated} poster${data.updated !== 1 ? "s" : ""}. You can now regenerate them.`);
      setBulkTplId("");
    } catch (err) {
      toast.error(err.response?.data?.detail || "Bulk assign failed");
    } finally {
      setBulkBusy(false);
    }
  };

  const mrOptions = employees.filter((e) => e.role === "mr");

  const PosterRow = ({ p, isMissing }) => {
    const override = rowTplOverride[p.id] || "";
    const isRegen = regenerating === p.id;
    const isDown = downloading === p.id;

    return (
      <tr className={`border-b border-slate-100 hover:bg-slate-50 ${isMissing ? "bg-amber-50/40" : ""}`}>
        <td className="px-4 py-2">
          <PosterThumb poster={p} onOpen={setViewing} />
        </td>
        <td className="px-4 py-3 font-medium text-slate-900">
          {p.doctor_name}
          {p.qualification && <div className="text-xs text-slate-400 font-normal">{p.qualification}</div>}
        </td>
        <td className="px-4 py-3 text-slate-600 max-w-[140px] truncate">{p.hospital || "—"}</td>
        <td className="px-4 py-3">
          <span className="inline-block px-2 py-0.5 rounded-full bg-slate-100 text-slate-600 text-xs uppercase">
            {p.language_code}
          </span>
        </td>
        <td className="px-4 py-3">
          <div className="text-slate-900 font-medium text-sm">{p.created_by_name}</div>
          <div className="text-xs text-slate-400">{p.created_by_emp_code}</div>
        </td>
        <td className="px-4 py-3 text-slate-500 whitespace-nowrap text-sm">
          {new Date(p.created_at).toLocaleDateString()}<br />
          <span className="text-xs text-slate-400">{new Date(p.created_at).toLocaleTimeString([], { hour: "2-digit", minute: "2-digit" })}</span>
        </td>
        <td className="px-4 py-3">
          {isMissing ? (
            <Badge variant="outline" className="border-amber-400 text-amber-600 gap-1 text-xs">
              <AlertTriangle className="w-3 h-3" /> No template
            </Badge>
          ) : p.file_deleted ? (
            <span className="inline-flex items-center gap-1 text-xs text-slate-400">
              <ImageOff className="w-3 h-3" /> Removed
            </span>
          ) : (
            <span className="inline-flex items-center gap-1 text-xs text-emerald-600">
              <CheckCircle2 className="w-3 h-3" /> Ready
            </span>
          )}
        </td>
        <td className="px-4 py-3">
          <div className="flex items-center justify-end gap-1 flex-wrap">
            {isMissing ? (
              /* Missing template row: show template picker + regenerate */
              <>
                <Select
                  value={override}
                  onValueChange={(v) => setRowTplOverride((prev) => ({ ...prev, [p.id]: v }))}
                >
                  <SelectTrigger className="h-8 text-xs w-36">
                    <SelectValue placeholder="Pick template" />
                  </SelectTrigger>
                  <SelectContent>
                    {templates.map((t) => (
                      <SelectItem key={t.id} value={t.id} className="text-xs">
                        {t.name || t.language_code}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
                <Button
                  size="sm" variant="default"
                  className="h-8 gap-1 text-xs bg-brand-600 hover:bg-brand-700"
                  onClick={() => handleRegenerate(p, override)}
                  disabled={!override || isRegen}
                  title="Assign template and regenerate"
                >
                  {isRegen ? <RefreshCw className="w-3.5 h-3.5 animate-spin" /> : <RefreshCw className="w-3.5 h-3.5" />}
                  <span className="hidden sm:inline">Regen</span>
                </Button>
              </>
            ) : (
              /* Normal row: download if available, regenerate */
              <>
                {!p.file_deleted && (
                  <Button
                    size="sm" variant="outline"
                    className="h-8 gap-1 text-xs"
                    onClick={() => handleDownload(p)}
                    disabled={!!isDown}
                    title="Download poster"
                  >
                    {isDown ? <RefreshCw className="w-3.5 h-3.5 animate-spin" /> : <Download className="w-3.5 h-3.5" />}
                    <span className="hidden sm:inline">Download</span>
                  </Button>
                )}
                <Button
                  size="sm" variant="outline"
                  className="h-8 gap-1 text-xs"
                  onClick={() => handleRegenerate(p, null)}
                  disabled={isRegen}
                  title="Regenerate poster"
                >
                  <RefreshCw className={`w-3.5 h-3.5 ${isRegen ? "animate-spin" : ""}`} />
                  <span className="hidden sm:inline">Regen</span>
                </Button>
              </>
            )}
            <Button
              size="sm" variant="ghost"
              className="h-8 w-8 p-0 text-red-400 hover:text-red-600 hover:bg-red-50"
              onClick={() => handleDelete(p.id)}
              title="Delete record"
            >
              <Trash2 className="w-4 h-4" />
            </Button>
          </div>
        </td>
      </tr>
    );
  };

  const TableHead = () => (
    <thead className="bg-slate-50 border-b border-slate-200">
      <tr className="text-left text-xs uppercase tracking-wider text-slate-500">
        <th className="px-4 py-3 w-20">Poster</th>
        <th className="px-4 py-3">Doctor</th>
        <th className="px-4 py-3">Hospital</th>
        <th className="px-4 py-3">Language</th>
        <th className="px-4 py-3">MR</th>
        <th className="px-4 py-3">Date</th>
        <th className="px-4 py-3">File</th>
        <th className="px-4 py-3 text-right">Actions</th>
      </tr>
    </thead>
  );

  return (
    <Shell role="admin">
      <div className="mb-6">
        <div className="text-xs font-bold uppercase tracking-[0.2em] text-slate-500 mb-2">Poster Records</div>
        <h1 className="font-display text-3xl font-extrabold tracking-tight text-slate-900">All Generated Posters</h1>
        <p className="text-slate-500 mt-1 text-sm">{filtered.length} record{filtered.length !== 1 ? "s" : ""}</p>
      </div>

      {/* Missing-template banner */}
      {missingTplPosters.length > 0 && (
        <Card className="p-4 mb-6 border-amber-300 bg-amber-50">
          <div className="flex flex-col sm:flex-row sm:items-center gap-3">
            <div className="flex items-start gap-2 flex-1">
              <AlertTriangle className="w-5 h-5 text-amber-500 shrink-0 mt-0.5" />
              <div>
                <div className="font-semibold text-amber-800 text-sm">
                  {missingTplPosters.length} poster{missingTplPosters.length !== 1 ? "s" : ""} have a deleted template
                </div>
                <div className="text-xs text-amber-700 mt-0.5">
                  Bulk-assign a replacement template, then regenerate individually — or pick a template per row below.
                </div>
              </div>
            </div>
            <div className="flex items-center gap-2 shrink-0">
              <Select value={bulkTplId} onValueChange={setBulkTplId}>
                <SelectTrigger className="h-9 w-48 text-sm bg-white">
                  <SelectValue placeholder="Select template" />
                </SelectTrigger>
                <SelectContent>
                  {templates.map((t) => (
                    <SelectItem key={t.id} value={t.id}>{t.name || t.language_code}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
              <Button
                size="sm"
                className="h-9 bg-amber-600 hover:bg-amber-700 text-white gap-1.5"
                onClick={handleBulkAssign}
                disabled={bulkBusy || !bulkTplId}
              >
                {bulkBusy ? <RefreshCw className="w-3.5 h-3.5 animate-spin" /> : null}
                Assign to all {missingTplPosters.length}
              </Button>
            </div>
          </div>
        </Card>
      )}

      {/* Filters */}
      <Card className="p-4 mb-6">
        <div className="flex flex-col md:flex-row gap-3">
          <Select value={mrId} onValueChange={setMrId}>
            <SelectTrigger className="h-11 md:w-52">
              <SelectValue placeholder="All MRs" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">All MRs</SelectItem>
              {mrOptions.map((e) => (
                <SelectItem key={e.id} value={e.id}>{e.name} ({e.emp_code})</SelectItem>
              ))}
            </SelectContent>
          </Select>

          <Select value={langFilter} onValueChange={setLangFilter}>
            <SelectTrigger className="h-11 md:w-44">
              <SelectValue placeholder="All Languages" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">All Languages</SelectItem>
              {languages.map((l) => (
                <SelectItem key={l.id} value={l.code}>{l.name}</SelectItem>
              ))}
            </SelectContent>
          </Select>

          <div className="relative flex-1">
            <Search className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" />
            <Input
              placeholder="Search by doctor name, hospital or MR…"
              value={q} onChange={(e) => setQ(e.target.value)}
              className="pl-9 h-11"
            />
          </div>

          <Input type="date" value={date} onChange={(e) => setDate(e.target.value)} className="h-11 md:w-44" />
        </div>
      </Card>

      {/* Missing-template table */}
      {missingTplPosters.length > 0 && (
        <div className="mb-6">
          <div className="flex items-center gap-2 mb-2 px-1">
            <AlertTriangle className="w-4 h-4 text-amber-500" />
            <span className="text-sm font-semibold text-amber-700">Posters with missing template</span>
          </div>
          <Card className="overflow-hidden border-amber-200">
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <TableHead />
                <tbody>
                  {missingTplPosters.map((p) => <PosterRow key={p.id} p={p} isMissing={true} />)}
                </tbody>
              </table>
            </div>
          </Card>
        </div>
      )}

      {/* Normal posters table */}
      <Card className="overflow-hidden">
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <TableHead />
            <tbody>
              {loading && (
                <tr><td colSpan={8} className="text-center py-10 text-slate-400">Loading…</td></tr>
              )}
              {!loading && normalPosters.length === 0 && missingTplPosters.length === 0 && (
                <tr><td colSpan={8} className="text-center py-10 text-slate-400">No records found.</td></tr>
              )}
              {!loading && normalPosters.length === 0 && missingTplPosters.length > 0 && (
                <tr><td colSpan={8} className="text-center py-10 text-slate-400">No other records.</td></tr>
              )}
              {!loading && normalPosters.map((p) => <PosterRow key={p.id} p={p} isMissing={false} />)}
            </tbody>
          </table>
        </div>
      </Card>

      <PosterLightbox
        poster={viewing}
        onClose={() => setViewing(null)}
        onDownload={handleDownload}
        downloading={viewing && downloading === viewing.id}
      />
    </Shell>
  );
}
